import { Link } from 'react-router-dom'

import type { ElPeriodo } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

import { aumento } from './cifras'
import estilos from './CifrasDelPeriodo.module.css'

/**
 * CARTERA RECAUDADA, EN EL TABLERO.
 *
 * Era «Próximamente» (Johan: «cartera recaudada acá, pero como a futuro»);
 * con el área de cartera (0246, 0247) ya tiene datos: lo recaudado en el
 * periodo —los pagos anotados, por su fecha— con su aumento, y lo que se
 * debe hoy por edades, desde que se finalizó cada servicio.
 *
 * Desde 0251 sale de la MISMA cuenta que la pantalla de Cartera
 * (fn_cuentas_de_cartera: solo FEV) y lo recaudado viene partido en bancos
 * y efectivo, como allá (Johan: «asócialo bien con el informe»).
 */
const EDADES = ['0–30 días', '31–60 días', '61–90 días', 'Más de 90']

export function CarteraDelPeriodo({ periodo: p }: { periodo: ElPeriodo }) {
  const ahora = Number(p.actual.recaudado.valor)
  const antes = Number(p.anterior.recaudado.valor)
  const { texto, tendencia } = aumento(ahora, antes)
  const edades = p.cartera.edades.map(Number)
  const maximo = Math.max(1, ...edades)

  return (
    <div className={estilos.bloque}>
      <p className={estilos.bloqueTitulo}>
        Cartera recaudada
        <Link to="/financiera/cartera" className={estilos.bloqueEnlace}>
          Abrir cartera ›
        </Link>
      </p>

      <div className={estilos.carteraCifras}>
        <div>
          <p className={estilos.carteraRotulo}>Recaudado en el periodo</p>
          <p className={estilos.carteraValor}>
            {pesos(ahora)}
            <span
              className={`${estilos.aumentoClaro} ${
                tendencia === 'igual'
                  ? ''
                  : tendencia === 'sube'
                  ? estilos.subeClaro
                  : estilos.bajaClaro
              }`}
            >
              {tendencia === 'sube' ? '▲ ' : tendencia === 'baja' ? '▼ ' : ''}
              {texto}
            </span>
          </p>
          <p className={estilos.carteraPie}>
            {p.actual.recaudado.cantidad} {p.actual.recaudado.cantidad === 1 ? 'pago' : 'pagos'} ·
            bancos <b>{pesos(Number(p.actual.recaudado.bancos ?? 0))}</b> · efectivo{' '}
            <b>{pesos(Number(p.actual.recaudado.efectivo ?? 0))}</b>
            {Number(p.actual.recaudado.otro ?? 0) > 0 && (
              <>
                {' '}
                · otro <b>{pesos(Number(p.actual.recaudado.otro))}</b>
              </>
            )}
          </p>
        </div>
        <div>
          <p className={estilos.carteraRotulo}>Por cobrar hoy</p>
          <p className={estilos.carteraValor}>{pesos(Number(p.cartera.por_cobrar))}</p>
          <p className={estilos.carteraPie}>
            {p.cartera.cuentas} {p.cartera.cuentas === 1 ? 'cuenta abierta' : 'cuentas abiertas'}
          </p>
        </div>
      </div>

      <div className={estilos.carteraEdades} aria-label="Lo que se debe, por edades">
        {edades.map((v, i) => (
          <div key={EDADES[i]} className={estilos.carteraEdad}>
            <span className={estilos.carteraEdadValor}>{v > 0 ? pesos(v) : '—'}</span>
            <span
              className={`${estilos.carteraEdadBarra} ${estilos[`edad${i}`]}`}
              style={{ height: `${v > 0 ? Math.max(8, (v / maximo) * 100) : 4}%` }}
            />
            <span className={estilos.carteraEdadRotulo}>{EDADES[i]}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
